import React, { useState } from 'react';
import { Box, IconButton, TableCell, TableRow, Tooltip } from 'components/mui';
import { CheckIcon, DeleteIcon, EditIcon } from 'components/icons';
import styles from './CommentsTable.module.css';
import { Card, CardContent, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CommentEditor from '../editor/CommentEditor';
import CloseIcon from '@mui/icons-material/Close';
import { CANtrackComment } from '../../types/comment';
import useCommentEditor from '../../hooks/useCommentEditor';
import DeleteCommentWarning from '../alerts/DeleteCommentWarning';

interface ActionButtonsProps {
	editMode: boolean;
	onEditClick: () => void;
	onConfirmEdit: () => void;
	onCancelEdit: () => void;
	onDeleteClick: () => void;
}

const ActionButtons = (props: ActionButtonsProps) => (
	<Box className={styles.editButtonsContainer}>
		{!props.editMode && (
			<>
				<Tooltip title="Edit">
					<IconButton size="small" onClick={props.onEditClick}>
						<EditIcon color="action" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Delete">
					<IconButton size="small" onClick={props.onDeleteClick}>
						<DeleteIcon color="error" />
					</IconButton>
				</Tooltip>
			</>
		)}
		{props.editMode && (
			<>
				<Tooltip title="Confirm Edit">
					<IconButton size="small" onClick={props.onConfirmEdit}>
						<CheckIcon />
					</IconButton>
				</Tooltip>
				<Tooltip title="Cancel Edit">
					<IconButton size="small" onClick={props.onCancelEdit}>
						<CloseIcon />
					</IconButton>
				</Tooltip>
			</>
		)}
	</Box>
);

const CommentsTableRowExpanded: React.FC<{
	comment: CANtrackComment;
	isEditing: boolean;
	setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
	setComments: React.Dispatch<React.SetStateAction<CANtrackComment[]>>;
}> = ({ comment, isEditing, setIsEditing, setComments }) => {
	const theme = useTheme();
	const [showDeleteConfirmation, setShowDeleteConfirmation] =
		useState<boolean>(false);

	const {
		errors,
		formState,
		handleChange,
		handleDeleteComment,
		handleSaveComment,
		handleResetComment,
	} = useCommentEditor(setComments, comment);

	const handleConfirmEdit = async () => {
		await handleSaveComment();
		setIsEditing(false);
	};

	return (
		<TableRow sx={{ backgroundColor: theme.palette.background.paper }}>
			<TableCell
				colSpan={7}
				sx={{ p: { xs: 1, md: 2 } }}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: { xs: 'column', md: 'row' },
						gap: 1,
					}}
				>
					<Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, mb: { xs: 1, md: 0 } }}>
						<ActionButtons
							editMode={isEditing}
							onEditClick={() => setIsEditing(true)}
							onCancelEdit={() => setIsEditing(false)}
							onConfirmEdit={handleConfirmEdit}
							onDeleteClick={() => setShowDeleteConfirmation(true)}
						/>
					</Box>
					<Box sx={{ flex: 1, width: '100%' }}>
						{isEditing ? (
							<CommentEditor
								comment={comment}
								errors={errors}
								formState={formState}
								handleChange={handleChange}
								handleSave={handleSaveComment}
								handleReset={handleResetComment}
								handleDelete={() => setShowDeleteConfirmation(true)}
							/>
						) : (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: '0.5rem',
								}}
							>
								{/* Mobile-only: Show additional info that's hidden in table */}
								<Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1, mb: 1 }}>
									<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
										<Typography variant="caption" color="textSecondary">Client:</Typography>
										<Typography variant="body2">{comment.customerName}</Typography>
									</Box>
									<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
										<Typography variant="caption" color="textSecondary">Case:</Typography>
										<Typography variant="body2">{comment.caseTitle}</Typography>
									</Box>
								</Box>
								<Card>
									<CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
										<Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
											Comment
										</Typography>
										{comment.description ? (
											<Box
												sx={{
													'& p': { m: 0 },
													'& img': { maxWidth: '100%', height: 'auto' },
													'& a': { color: 'primary.main' },
													typography: 'body2',
												}}
												dangerouslySetInnerHTML={{ __html: comment.description }}
											/>
										) : (
											<Typography variant="body2" color="text.secondary">
												No comment
											</Typography>
										)}
									</CardContent>
								</Card>
							</Box>
						)}
					</Box>
				</Box>
			</TableCell>
			<DeleteCommentWarning
				open={showDeleteConfirmation}
				onClose={() => setShowDeleteConfirmation(false)}
				onConfirm={() => {
					setShowDeleteConfirmation(false);
					handleDeleteComment();
				}}
			/>
		</TableRow>
	);
};

export default CommentsTableRowExpanded;
